<template>
    <div>
        <!-- 칸이 16개인 넓은 표라 본문을 화면 폭으로(관리자 셸 본문처럼 좌우 16) — 제목도 같은 세로선 -->
        <admin-page-header title="프로젝트 뷰어"/>

        <b-container fluid class="ds-top-body">
            <b-tabs v-if="projects_periods.length" id="project-code-list-tab" v-model="selected_period"
                    content-class="py-3">
                <div v-if="is_admin === 4"
                     style="margin-top: -54px;"
                     class="text-right"
                >

                    <b-button
                        size="sm"
                        variant="outline-primary"
                        :disabled="downloading_statistics === true"
                        @click="downloadStatistics(projects_periods[selected_period])"
                    >
                        <span v-if="downloading_statistics !== true">
                            PERIOD 산업 통계 다운로드 <fa icon="file-excel" class="ml-1"/>
                        </span>
                        <span v-else>
                            <b-spinner small></b-spinner> 파일을 준비하는 중... (최대 1분이 소요됩니다)
                        </span>
                    </b-button>
                </div>
                <b-card no-body bg-variant="white"
                        :style="{marginTop: is_admin === 4 ? '28px' : '0px'}"
                >
                    <div class="p-3">
                        <b-form id="project-search-form"
                                autocomplete="new-password"
                                @submit.stop.prevent="searchData"
                                @reset="resetForm"
                        >
                            <div class="d-flex justify-content-between align-items-end">
                                <b-form-group
                                    id="fieldset-input-project_code"
                                    :label="$t('프로젝트 코드 검색')"
                                    label-for="ais-input-project_code"
                                    class="mb-0 w-100 mr-3"
                                >
                                    <b-form-input id="ais-input-project_code"
                                                  v-model="searchFormValue.project_code"
                                                  :disabled="listLoading"
                                                  trim
                                    />
                                </b-form-group>

                                <div>
                                    <b-button
                                        variant="primary"
                                        :disabled="listLoading"
                                        form="project-search-form"
                                        type="submit"
                                    >
                                        {{ $t('검색하기') }}
                                    </b-button>
                                </div>
                            </div>
                        </b-form>
                    </div>
                </b-card>

                <b-tab v-for="(period, period_idx) in projects_periods"
                       :key="period"
                       :title="period"
                       :active="period_idx === selected_period"
                >


                    <!-- DS 표 규칙: 행 44 고정 · 한 줄 머리 · 칸마다 한 줄(넘치면 줄이고 전체는 title). 폭은 머리가 정한다 -->
                    <b-table id="project-table" :fields="project_code_fields" :items="searchResults"
                             :table-class="['ds-table', 'ds-table-fixed', `project-table-${table_variant}`]"
                             hover
                             responsive
                             show-empty
                             :busy="listLoading"
                    >
                        <template #empty>
                            <PageEmptyComponent
                                :text="$t('조회된 프로젝트가 없습니다.')"
                                :loading="listLoading"
                                :loadingText="$t('프로젝트를 조회중입니다.')"
                                :error="load_error"
                                @retry="fetchData"
                            />
                        </template>

                        <!-- 프로젝트 코드만(원청 · 담당자 칸은 뺐다). 화면 이동은 관리 칸 묶음에서 -->
                        <template #cell(project_code)="data">
                            <span class="ds-ellipsis" :title="data.value">{{ data.value }}</span>
                        </template>

                        <!-- 마감일만. 지났으면 빨강, 며칠 남았는지 · 지났는지는 마우스를 올리면(title) -->
                        <template #cell(target_end_date)="data">
                            <span :class="getDeadline(data.value).tone" :title="getDeadline(data.value).title">{{ getDeadline(data.value).text }}</span>
                        </template>

                        <!-- 진행현황 · 기업별 답변 · 평가결과는 한 화면의 탭이라 한 버튼으로 묶는다(사용자 결정 2026-10-21 시안 1):
                             글자 = 진행현황 바로(링크라 새 탭 · 링크 복사가 된다), ▾ = 기업별 답변 · 평가결과.
                             보고서 일괄생성은 다른 일이라 떼어 둔다(최고 관리자만) -->
                        <template #cell(managing)="data">
                            <div class="d-flex align-items-center" style="gap: 12px">
                                <b-dropdown split :split-to="projectRoute('ProjectProgress', data.item)" text="진행현황"
                                            size="sm" variant="outline-secondary" split-class="ds-row-btn" toggle-class="ds-row-btn ds-row-btn-caret"
                                            boundary="viewport" right
                                            :toggle-attrs="{ 'aria-label': `${data.item.project_code} 다른 화면` }">
                                    <b-dropdown-item :to="projectRoute('Answers', data.item)">기업별 답변</b-dropdown-item>
                                    <b-dropdown-item :to="projectRoute('CampaignReport', data.item)">평가결과</b-dropdown-item>
                                </b-dropdown>
                                <b-button v-if="is_admin === 4" :to="projectRoute('ReportBulkRun', data.item)"
                                          size="sm" variant="outline-secondary" class="ds-row-btn"
                                          :aria-label="`${data.item.project_code} 보고서 일괄생성`">보고서 일괄생성</b-button>
                            </div>
                        </template>

                        <!-- 메모는 한 줄(태그를 뺀 전체 글자는 title). 편집 → 메모 창 -->
                        <template #cell(memo)="data">
                            <div class="d-flex align-items-center" style="gap: 8px">
                                <div class="ds-ellipsis project-memo-line" :title="memoText(data.value)" v-html="data.value"></div>
                                <b-button v-if="is_admin === 4" size="sm" variant="outline-secondary" class="ds-row-btn flex-shrink-0 ml-auto"
                                          :disabled="data.item.is_completed || saving_memo"
                                          :aria-label="`${data.item.project_code} 관리자 메모 편집`"
                                          @click="openMemoEditor(data.item)">편집</b-button>
                            </div>
                        </template>
                    </b-table>
                    <div class="d-flex justify-content-end align-items-center mt-3">
                        <b-pagination
                            ref="pagination"
                            v-model="pagination.currentPage"
                            aria-controls="project-table"
                            :total-rows="pagination.totalRows"
                            :per-page="pagination.perPage"
                            first-number
                            last-number
                            :disabled="listLoading"
                            align="right"
                            @change="(page)=>changeQuery({page})"
                        ></b-pagination>
                        <b-form-select
                            v-model="pagination.perPage"
                            class="ml-3 mb-3"
                            style="width: 100px"
                            size="sm"
                            :disabled="listLoading"
                            @change="(pageSize)=>changeQuery({pageSize})"
                        >
                            <b-form-select-option v-for="data in perPageOptions"
                                                  :key="`${data.text}-${data.value}`"
                                                  :value="data.value">{{ data.text }}
                            </b-form-select-option>
                        </b-form-select>
                    </div>
                </b-tab>
            </b-tabs>
        </b-container>

        <!-- 관리자 메모 편집 — 초안(memo_drafts)이 있는 동안만 열린다. 저장에 성공하면 초안이 지워져 저절로 닫힌다 -->
        <b-modal :visible="memo_editing"
                 :title="`관리자 메모 · ${memo_item ? memo_item.project_code : ''}`"
                 no-close-on-backdrop
                 :no-close-on-esc="saving_memo"
                 :hide-header-close="saving_memo"
                 @hidden="closeMemoEditor"
        >
            <b-form-textarea v-if="memo_editing" v-model="memo_drafts[memo_item.project_code]" rows="6" class="f-90"
                             :disabled="memo_item.is_completed || saving_memo"></b-form-textarea>
            <template #modal-footer>
                <b-button variant="dark-border" size="sm" :disabled="saving_memo" @click="closeMemoEditor">취소</b-button>
                <b-button variant="blue" size="sm"
                          :disabled="!memo_editing || memo_item.is_completed || saving_memo"
                          @click="handleSaveProjectMemo(memo_item)">
                    <b-spinner v-if="saving_memo" small class="mr-1"></b-spinner>저장
                </b-button>
            </template>
        </b-modal>
        <hr class="border-0 py-5">
    </div>
</template>

<script>
import {mapState, mapActions, mapMutations} from 'vuex';
import downloadFile from '@shared_mixins/downloadFile';
import {getDefaultPagination, getPerPageOptions} from '@shared/utils/common';
import axios from '@core/utils/api/_axios';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import {getErrorMessage} from '@core/utils/api/errorMessage';
import dayjs from 'dayjs';

const projects_periods = ['2024_1H', '2025_1H', '2026_1H']
export default {
    name: 'ProjectCodeList',
    components: {PageEmptyComponent},
    mixins: [
        downloadFile
    ],
    props: {
        urlError: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            basic_status: ['에코바디스', '미신청', '신청됨', '답변중', '보완중', '평가중', '완료', '취소'],
            basic_types: ['일반', '심화'],
            // 편집 중인 메모 초안 { [project_code]: 문자열 } — 키가 있으면 편집 중(메모 창이 열린다)
            memo_drafts: {},
            // 메모 창에서 고치는 행
            memo_item: null,
            downloading_statistics: false,
            saving_memo: false,

            listLoading: false,
            projects_periods: projects_periods,
            selected_period: projects_periods.length - 1,

            searchResults: null,
            load_error: '',
            searchFormValue: {
                project_code: '',
                period: null,
            },
            pagination: getDefaultPagination(1, 20),
            perPageOptions: getPerPageOptions(),
        };
    },
    computed: {
        ...mapState(['user_id', 'is_admin']),
        ...mapState('project_code', ['project_codes', 'is_project_codes_loaded', 'project_companies']),
        // 칸 구성에 따라 표 최소 폭이 다르다(아래 style 의 project-table-*): 최고 관리자 = 메모 + 일괄생성, 3 = 메모, 그 밖 = 기본
        table_variant() {
            if (this.is_admin === 4) return 'full';
            return this.is_admin >= 3 ? 'memo' : 'basic';
        },
        // DS 표 규칙(한 줄 머리 · 번호 → 메인 → 데이터 → 행 액션은 맨 끝). 폭을 안 적은 칸이 남는 폭을 갖는다 —
        // 메모가 있으면 메모(긴 글), 없으면 프로젝트. 프로젝트 코드는 220 이면 거의 다 보이고 넘치면 줄인다.
        // 상태 · 타입 묶음 머리는 단어 그대로 두고 묶음 이름은 title 로
        project_code_fields() {
            const has_memo = this.is_admin >= 3;
            const count = (key, headerTitle, width = '56px') => ({
                key, label: key, headerTitle,
                thClass: 'ds-cell-num project-count-cell', tdClass: 'ds-cell-num project-count-cell', thStyle: {width},
            });
            const fields = [
                {key: 'index', label: 'No.', thClass: 'ds-cell-no', tdClass: 'ds-cell-no', thStyle: {width: '56px'}},
                {key: 'project_code', label: '프로젝트', thStyle: has_memo ? {width: '220px'} : {}},
                {key: 'target_end_date', label: '마감', tdClass: 'ds-cell-nowrap', thStyle: {width: '104px'}},
                {...count('num_of_companies'), label: 'Total'},
                ...this.basic_status.map(key => key === '에코바디스'
                    ? count(key, `상태 · ${key}`, '84px')
                    // 에코바디스 외 상태는 0 을 '-' 로(기존 표시 그대로)
                    : {...count(key, `상태 · ${key}`), formatter: value => value || '-'}),
                ...this.basic_types.map(key => count(key, `타입 · ${key}`)),
            ];
            if (has_memo) fields.push({key: 'memo', label: '관리자 메모'});
            // 관리 칸 = [진행현황 | ▾](~84) + 보고서 일괄생성(~94, 최고 관리자만, 간격 12) + 좌우 여백 32
            fields.push({key: 'managing', label: '관리', thStyle: {width: this.is_admin === 4 ? '220px' : '120px'}});
            return fields;
        },
        // 메모 창은 초안이 있는 동안만 열린다
        memo_editing() {
            return this.memo_item != null && this.memo_drafts[this.memo_item.project_code] != null;
        },
    },
    watch: {
        '$route': {
            handler() {
                this.setSearchDataFormQuery();
                this.fetchData();
            }
        },
        // 탭을 눌러 기간을 바꿀 때만 이동한다. URL 에서 읽어 온 기간(직접 진입·뒤로가기)은 이미 URL 과 같으므로
        // 다시 이동하지 않는다 — 이동하면 per_page 가 붙은 새 주소로 $route watcher 가 한 번 더 조회했다
        selected_period(newVal) {
            if (newVal == null || newVal === this.routePeriodIndex()) return;
            this.searchFormValue.period = this.projects_periods[newVal];
            this.changeQuery({});
        },
    },
    async created() {
        this.perPageOptions.splice(-1, 1);
        // 첫 렌더 전에 URL 의 기간을 탭에 넣는다. mounted 에서 넣으면 b-tabs 가 기본 탭(마지막 기간)으로 먼저 만들어진 뒤
        // 옛 값을 v-model 로 되돌려 보내 ?period=2025_1H 가 2026_1H 로 바뀌었다
        this.setSearchDataFormQuery();

        if (this.$route.params?.urlError) {
            this.$showModal({
                type: 'alert',
                state: 'warning',
                title: this.$t('잘못된 접근'), // content는 기본값 사용, title만 변경
                message: this.$t('URL에 필요한 정보가 부족합니다. 개발팀에 문의해주세요.'),
            });
        }


    },
    async mounted() {
        try {
            this.set_admin_mode('project');
            if (Object.keys(this.$route.query).length > 0)
                await this.fetchData();
            else
                await this.changeQuery({});
            this.update_is_premium(true);
        } catch (error) {
            console.log(error);
        }
    },
    beforeDestroy() {
        this.update_custom_project_codes_list([]);
        this.update_is_project_codes_loaded(false);
    },
    methods: {
        ...mapMutations('project_code', ['update_is_project_codes_loaded', 'update_custom_project_codes_list', 'update_now_project']),
        ...mapMutations('score', ['add_all_temp_target_companies']),
        ...mapMutations(['set_admin_mode']),
        ...mapMutations('payment', ['update_is_premium']),
        ...mapActions('project_code', ['readProjectCodes', 'readProjectCompaniesCampaigns']),
        async searchData() {
            this.pagination.currentPage = 1;
            await this.changeQuery({});
        },
        // URL 의 기간 탭 번호(없거나 모르는 값이면 마지막 기간)
        routePeriodIndex() {
            const index = this.projects_periods.indexOf(this.$route.query.period);
            return index === -1 ? this.projects_periods.length - 1 : index;
        },
        setSearchDataFormQuery() {
            this.searchFormValue.project_code = this.$route.query.project_code || null;
            this.selected_period = this.routePeriodIndex();

            this.searchFormValue.period = this.projects_periods[this.selected_period];
            this.pagination.currentPage = Number(this.$route.query.current_page) || 1;
            this.pagination.perPage = Number(this.$route.query.per_page) || 20;
        },
        async changeQuery({page, pageSize}) {
            const query = {};
            let checkSameQuery = true;
            const toQueryObj = {
                ...this.searchFormValue,
                current_page: page ?? this.pagination.currentPage,
                per_page: pageSize ?? this.pagination.perPage
            };

            Object.entries(toQueryObj).forEach(([key, item]) => {
                if (item != null && item !== '') {
                    if (query[key] !== toQueryObj[key]) {
                        checkSameQuery = false;
                        query[key] = toQueryObj[key];
                    }
                }
            });

            if (!checkSameQuery) {
                await this.$router.push({query}).catch(e => {
                });
            }
        },
        resetForm() {
            this.searchFormValue = {
                project_code: null,
            };
        },
        async fetchData() {
            try {
                this.listLoading = true;
                this.load_error = '';
                this.memo_drafts = {};

                this.searchResults = null;

                const res = await axios.get('/projects', {
                    params: {
                        user_id: this.user_id,
                        period: this.searchFormValue.period,
                        project_code: this.searchFormValue.project_code,
                        page: this.pagination.currentPage,
                        per_page: this.pagination.perPage,
                    }
                });

                const {items, total, page} = res.data;

                this.searchResults = items.map((item, index) => {
                    const data = {
                        ...item,
                        // No. 는 표시 순서 — 페이지를 넘어 이어진다
                        index: (Number(page) - 1) * this.pagination.perPage + index + 1,
                    };
                    return data;
                });

                
                
                this.pagination.totalRows = Number(total);
                this.pagination.currentPage = null;
                setTimeout(()=>{
                    this.pagination.currentPage = Number(page);   
                });
            } catch (e) {
                if (e?.response?.status === 401) return;
                this.load_error = getErrorMessage(e, '프로젝트 목록을 불러오지 못했습니다.');
                this.$EventBus.$emit('toast-failure', this.$t('검색 오류'), '프로젝트 검색 도중 오류가 발생했습니다. 잠시후 재시도 해주세요.');
            } finally {
                this.listLoading = false;

            }

        },
        // 마감까지 남은 날짜 수(달력 기준, 오늘 마감 = 0). 마감일이 없으면 null.
        // 옆의 날짜 줄(formatDate)처럼 서버가 붙인 'GMT' 를 떼고 읽는다 — 그대로 읽으면 15시 이후 시각이 다음 날로 넘어가 하루 차이가 났다
        getLeftDays(target_end_date) {
            const end = target_end_date ? dayjs(String(target_end_date).replace('GMT', '')) : null;
            return end && end.isValid() ? end.startOf('day').diff(dayjs().startOf('day'), 'day') : null;
        },
        getLeftDaysText(left_days) {
            if (left_days == null) return '상시';
            if (left_days > 0) return `${left_days}일 남음`;
            return left_days === 0 ? '오늘 마감' : `마감 ${-left_days}일 지남`;
        },
        // 마감 칸: 마감일만, 지났으면 빨강(사용자 요청 2026-10-21). 남은 · 지난 일수는 title(마우스를 올리면).
        // 마감일이 없으면 '상시'(흐리게)
        getDeadline(target_end_date) {
            const left_days = this.getLeftDays(target_end_date);
            if (left_days == null) return {text: '상시', tone: 'ds-sub', title: null};
            return {
                text: this.formatDate(target_end_date, 'YYYY-MM-DD'),
                tone: left_days < 0 ? 'ds-status-danger' : null,
                title: this.getLeftDaysText(left_days),
            };
        },
        // 관리 칸 버튼들이 가는 곳(진행현황 · 기업별 답변 · 평가결과 · 보고서 일괄생성)
        projectRoute(mode, project) {
            return {
                name: mode,
                query: {client: project.client, pc: project.project_code, cmp: project.campaign, admin_mode: 'project'}
            };
        },
        // 메모는 HTML 로 보인다 — 툴팁에는 태그를 뺀 글자(줄바꿈 태그는 줄바꿈으로)
        memoText(memo) {
            if (!memo) return '';
            return new DOMParser().parseFromString(String(memo).replace(/<br\s*\/?>/gi, '\n'), 'text/html').body.textContent;
        },
        // 취소는 초안만 버리고 표의 메모는 저장본 그대로 둔다
        toggleEditor(item) {
            if (this.memo_drafts[item.project_code] == null) this.$set(this.memo_drafts, item.project_code, item.memo || '');
            else this.$delete(this.memo_drafts, item.project_code);
        },
        openMemoEditor(item) {
            this.memo_item = item;
            this.toggleEditor(item);
        },
        // 취소 · ESC · 닫기(X) 는 초안을 버린다
        closeMemoEditor() {
            if (this.memo_editing) this.toggleEditor(this.memo_item);
        },
        async handleSaveProjectMemo(item) {
            if (this.saving_memo) return;
            this.saving_memo = true;
            try {
                // 빈 메모도 저장할 수 있다 (메모 삭제)
                const memo = this.memo_drafts[item.project_code] || '';
                await this.saveProjectMemo(item.project_code, memo);
                item.memo = memo;
                this.$delete(this.memo_drafts, item.project_code);
            } catch (error) {
                if (error?.response?.status === 401) return;
                this.$EventBus.$emit('toast-failure', '메모 저장 실패', getErrorMessage(error, '메모를 저장하지 못했습니다. 입력한 내용은 그대로 유지됩니다.'));
            } finally {
                this.saving_memo = false;
            }
        },
        async saveProjectMemo(project_code, memo) {
            let formData = new FormData;
            formData.append('user_id', this.user_id);
            formData.append('project_code', project_code);
            formData.append('memo', memo);

            const response = await this.$axios.post('/save_project_memo', formData);
            this.$EventBus.$emit('toast-success', '저장완료', response.data.msg);
        },
        async downloadStatistics(period) {
            try {
                this.downloading_statistics = true;
                const response = await this.$axios.get(`/scores/excel/periods/${period}`, {
                    responseType: 'blob'
                });

                this.downloadFileFromResponse(response, `${period}_산업_통계_데이터`);
            } catch (error) {
                console.log(error);
                this.$EventBus.$emit('toast-failure', '다운로드 실패', getErrorMessage(error, '산업 통계 파일을 내려받지 못했습니다. 잠시 후 다시 시도해 주세요.'));
            } finally {
                this.downloading_statistics = false;
            }
        },
    }
};
</script>

<style>
#project-code-list-tab__BV_tab_controls_ > li > .nav-link {
    color: var(--gray-600);
}

#project-code-list-tab__BV_tab_controls_ > li > .nav-link.active {
    background-color: var(--tealgray400);
    color: white;
    font-weight: 600;
    border-color: var(--tealgray400) var(--tealgray400) var(--tealgray400);

}

#project-code-list-tab__BV_tab_controls_ {
    border-bottom: 1px solid var(--tealgray400);
}

/* 고정 열 합 + 남는 폭을 갖는 칸(메모 160 · 프로젝트 200)의 최소 — 좁으면 가로 스크롤.
   고정: No. 56 · 마감 104 · 숫자 11칸 644 = 804 에 더해
   full = 프로젝트 220 + 관리 220([진행현황 | ▾] + 보고서 일괄생성) + 메모, memo = 프로젝트 220 + 관리 120 + 메모, basic = 관리 120 + 프로젝트.
   1440 화면 본문 폭(1408) 안에 든다 */
#project-table.project-table-full {
    min-width: 1404px;
}

#project-table.project-table-memo {
    min-width: 1304px;
}

#project-table.project-table-basic {
    min-width: 1124px;
}

/* 숫자 칸(56)은 좌우 여백을 8 로 — 공통 16 이면 '미신청' 같은 머리 단어가 칸을 넘친다 */
#project-table .project-count-cell {
    padding-left: 8px;
    padding-right: 8px;
}

/* 메모 안의 줄바꿈 · 블록 태그도 한 줄로 잇는다(행 44 고정) */
.project-memo-line {
    min-width: 0;
}

.project-memo-line * {
    display: inline;
}

.project-memo-line br {
    display: none;
}

</style>
